import { GTMProvider } from '@elgorditosalsero/react-gtm-hook';
import { ThemeProvider } from '@mui/material';
import axios, { AxiosError } from 'axios';
import axiosRetry from 'axios-retry';
import 'prevent-pull-refresh';
import React, {
    useContext,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { hotjar } from 'react-hotjar';
import { Route, Routes } from 'react-router-dom';
import './App.css';
import ThemeContext from './CommonAssets/Styles/ThemeContext';
import DeviceInfoPage from './Pages/MainComponents/DeviceInfo/DeviceInfoPage';
import useAppFineTuningTransitionHandler from './Pages/MainComponents/MainView/AppFineTuningTransitionHandlerHook';
import useAppFittingTransitionHandler from './Pages/MainComponents/MainView/AppFittingTransitionHandlerHook';
import MainView from './Pages/MainComponents/MainView/MainView';
import { MainViewWorkflow } from './Pages/MainComponents/MainView/MainViewModel';
import useWebTransitionHandler from './Pages/MainComponents/MainView/WebTransitionHandlerHook';
import AuthService from './Services/Authentication/AuthService';
import LoggingService from './Services/LoggingService';
import MaintenanceService from './Services/Maintenance/MaintenanceService';
import TelemetryProvider from './Services/Monitoring/TelemetryProvider';
import AlertMessagePopUp from './SharedComponents/Dialog/AlertMessage/AlertMessageDialog';
import MaintenanceDialog from './SharedComponents/Dialog/MainatenanceDialog/MaintenanceDialog';
import { IMaintenanceDialogResource } from './SharedComponents/Dialog/MainatenanceDialog/MaintenanceDialogResourceHook';
import PageNotFoundDialog from './SharedComponents/Dialog/PageNotFoundDialog/PageNotFoundDialog';
import Polling from './Utils/Polling';
import { updateNotification } from './Redux/Reducers/NotificationSlice';
import easyFitStore from './Redux/Store/EasyFitStore';
import { loadNonMedicalResources } from './Services/NonMedicalService';
import useDeleteAccountTransitionHandler from './Pages/MainComponents/MainView/DeleteAccountTransitionHandler';
import { t } from './Services/LocalizationService';
import { NotificationCloseHandlerType } from './Redux/Models/Notification/NotificationCloseHandlerType';
import { RootState } from './Redux/Reducers/RootReducer';
import { updateSetup } from './Redux/Reducers/SetupSlice';
import { preloadImages } from './Utils/PreloadingImageUtils';

axiosRetry(axios, {
    retries: Number(process.env.REACT_APP_MaxRetryCount) + 1,
    shouldResetTimeout: true,
    retryCondition: (error: AxiosError) => {
        return (
            axiosRetry.isNetworkOrIdempotentRequestError(error) ||
            error.code === 'ECONNABORTED' ||
            error.code === 'ENOTFOUND'
        );
    },
    retryDelay: (retryCount: number, error) => {
        LoggingService.warn({
            componentName: axiosRetry.name,
            args: [`Retry attempt: ${retryCount}\nerror: ${error}`],
        });
        return retryCount * 500;
    },
});

const App = (): JSX.Element => {
    const gtmId = process.env.REACT_APP_GTM_ID || '';
    const gtmParams = { id: gtmId };
    const appInsightsKey = process.env.REACT_APP_APP_INSIGHT_KEY
        ? process.env.REACT_APP_APP_INSIGHT_KEY
        : '';

    if (
        process.env.REACT_APP_HOTJAR_ID !== undefined &&
        process.env.ReACT_APP_HOTJAR_SV !== undefined
    ) {
        const hjid = Number(process.env.REACT_APP_HOTJAR_ID);
        const hjsv = Number(process.env.ReACT_APP_HOTJAR_SV);
        const options = { id: hjid, sv: hjsv };
        hotjar.initialize(options);
    }

    const dispatch = useDispatch();

    const maintenanceDialogDisplay = useRef(false);

    const [maintenanceDisplay, setMaintenanceDisplay] =
        useState<IMaintenanceDialogResource>({
            maintenancePeriod: 0,
        });

    const userExistsState = useSelector(
        (state: RootState) => state.setup.userExistsType
    );

    const axiosRequestInterceptor = useCallback(() => {
        axios.interceptors.request.use((config) => {
            if (!userExistsState) {
                LoggingService.warn({
                    componentName: 'axiosRequestInterceptor',
                    args: [`Invalid user account`],
                });
                easyFitStore.dispatch(
                    updateNotification({
                        ...easyFitStore.getState().notification,
                        alertMessage: {
                            title: '',
                            message: [
                                t(
                                    'common:Common_Feature:ErrorHandling:Invalid_User_Text'
                                ),
                            ],
                            detailMessage: '',
                            userSelections: [
                                {
                                    content: t('common:Common_Feature:ok'),
                                    action: NotificationCloseHandlerType.UserAccountDeletedExit,
                                },
                            ],
                            isDisplayed: true,
                            workflow: 'App',
                        },
                    })
                );
            }
            return Promise.resolve(config);
        });
    }, [userExistsState]);

    const axiosResponseInterceptor = useCallback(() => {
        axios.interceptors.response.use(
            async (response) => {
                if (
                    response.status > 300 &&
                    !maintenanceDialogDisplay.current
                ) {
                    LoggingService.warn({
                        componentName: App.name,
                        args: [
                            `API returned status code ${response.status} with text ${response.statusText}`,
                        ],
                    });
                    maintenanceDialogDisplay.current = true;

                    const resp =
                        await MaintenanceService.GetMaintenancePageResponse();

                    if (
                        resp.IsAppInMaintenanceMode &&
                        resp.MaintenancePeriodEndTime > Date.now() / 1000
                    ) {
                        setMaintenanceDisplay({
                            ...maintenanceDisplay,
                            maintenancePeriod: resp.MaintenancePeriodEndTime,
                        });
                        dispatch(
                            updateNotification({
                                ...easyFitStore.getState().notification,
                                maintenanceMessage: {
                                    isDisplayed: resp.IsAppInMaintenanceMode,
                                    maintenancePeriod:
                                        resp.MaintenancePeriodEndTime,
                                },
                            })
                        );
                    }
                }
                return Promise.resolve(response);
            },
            (error) => {
                LoggingService.error({
                    componentName: App.name,
                    args: [`${error}`],
                });
                return Promise.reject(error);
            }
        );
    }, [dispatch, maintenanceDisplay]);

    const maintenanceHandler = {
        maintenancePeriod: maintenanceDisplay?.maintenancePeriod,
        display: maintenanceDisplay?.maintenancePeriod ? true : false,
        onClose: () => {
            maintenanceDialogDisplay.current = false;
            setMaintenanceDisplay({
                ...maintenanceDisplay,
                maintenancePeriod: 0,
            });
        },
    };

    const renderMaintenanceDialog = () => {
        const { display, onClose, maintenancePeriod } = maintenanceHandler;
        return (
            <MaintenanceDialog
                displayed={display}
                onClose={onClose}
                maintenancePeriod={maintenancePeriod}
            />
        );
    };

    const { theme } = useContext(ThemeContext);

    Polling(async () => {
        if (AuthService.adB2cInstance.getIsAuthenticated()) {
            const token = await AuthService.adB2cInstance.acquireAccessToken();
            if (token && userExistsState) {
                const userAccountStatus =
                    await AuthService.adB2cInstance.getUserAccountStatus();
                if (!userAccountStatus) {
                    easyFitStore.dispatch(
                        updateSetup({
                            ...easyFitStore.getState().setup,
                            userExistsType: false,
                        })
                    );
                }
            }
        }
    }, 60000 * 1);

    useEffect(() => {
        preloadImages();
        axiosRequestInterceptor();
        axiosResponseInterceptor();
        loadNonMedicalResources();
    }, [axiosRequestInterceptor, axiosResponseInterceptor]);

    return (
        <GTMProvider state={gtmParams}>
            <ThemeProvider theme={theme}>
                <div className="App-body">
                    <AlertMessagePopUp />
                    {renderMaintenanceDialog()}
                    <TelemetryProvider instrumentationKey={appInsightsKey}>
                        <Routes>
                            <Route
                                path="/app/fitting"
                                element={
                                    <MainView
                                        workflowType={
                                            MainViewWorkflow.AppFitting
                                        }
                                        useTransitionHandler={
                                            useAppFittingTransitionHandler
                                        }
                                    />
                                }
                            />
                            <Route
                                path="/app/finetuning"
                                element={
                                    <MainView
                                        workflowType={
                                            MainViewWorkflow.AppFineTuning
                                        }
                                        useTransitionHandler={
                                            useAppFineTuningTransitionHandler
                                        }
                                    />
                                }
                            />
                            <Route
                                path="/deviceinfo"
                                element={<DeviceInfoPage />}
                            />
                            <Route
                                path="/delete-account"
                                element={
                                    <MainView
                                        workflowType={
                                            MainViewWorkflow.DeleteAccount
                                        }
                                        useTransitionHandler={
                                            useDeleteAccountTransitionHandler
                                        }
                                    />
                                }
                            />
                            <Route
                                path="/"
                                element={
                                    <MainView
                                        workflowType={MainViewWorkflow.Web}
                                        useTransitionHandler={
                                            useWebTransitionHandler
                                        }
                                    />
                                }
                            />
                            <Route path="*" element={<PageNotFoundDialog />} />
                        </Routes>
                    </TelemetryProvider>
                </div>
            </ThemeProvider>
        </GTMProvider>
    );
};

export default App;
